<template>
  <fullscreen-dialog
    :value="value"
    @input="$emit('input', $event)"
    @save="save"
    content-background="#f5f5f5"
    :title="$t('settings')"
    :subtitle="`${company.name || ''} | ID: ${company.id || ''}`"
  >
    <v-row>
      <!-- Left Column -->
      <v-col cols="12" xl="6" lg="6" class="pr-lg-3">
        <!-- Basic Settings Section -->
        <div class="section-container pb-2 mb-4 ordera-box-shadow">
          <div
            class="section-title d-flex align-center px-6 py-3 subtitle-1 font-weight-medium"
          >
            <v-icon color="#FF6B35" class="mr-2">mdi-cog</v-icon>
            <span>{{ $t("basic_settings") }}</span>
          </div>
          <v-divider></v-divider>
          <div class="pa-5">
            <basic-settings-tab
              :settings="localCompany.settings"
              :company-type="localCompany.type"
              @change-show-price="changeShowPrice"
            />
          </div>
        </div>

        <!-- Shift Ordering Section -->
        <div class="section-container ordera-box-shadow pb-2 mb-4">
          <div
            class="section-title d-flex align-center px-6 py-3 subtitle-1 font-weight-medium"
          >
            <v-icon color="#FF6B35" class="mr-2">mdi-calendar-clock</v-icon>
            <span>{{ $t("shift_ordering") }}</span>
          </div>
          <v-divider></v-divider>
          <div class="pa-5">
            <shift-ordering-settings-tab
              :settings="localCompany.settings"
              :company="localCompany"
              :delivery-hours="deliveryHours"
              :delivery-minutes="deliveryMinutes"
              @order-meal-change="changeOrderMealFromOneShiftToAnother"
              @current-shift-change="changeCurrentShiftMeal"
            />
          </div>
        </div>
      </v-col>

      <!-- Right Column -->
      <v-col cols="12" xl="6" lg="6" class="pl-lg-3">
        <!-- Weekly Time Settings Section -->
        <div class="section-container pb-2 mb-4">
          <div
            class="section-title d-flex align-center px-6 py-3 subtitle-1 font-weight-medium"
          >
            <v-icon color="#FF6B35" class="mr-2">mdi-clock-time-four</v-icon>
            <span>{{ $t("weekly_time_settings") }}</span>
          </div>
          <v-divider></v-divider>
          <div class="pa-5">
            <weekly-time-settings-tab
              :settings="localCompany.settings"
              :delivery-hours="deliveryHours"
              :delivery-minutes="deliveryMinutes"
              :week-days="weekDays"
            />
          </div>
        </div>

        <!-- Questionnaire Section (Admin Only) -->
        <div v-if="user.type === 'admin'" class="section-container ordera-box-shadow pb-2 mb-4">
          <div
            class="section-title d-flex align-center px-6 py-3 subtitle-1 font-weight-medium"
          >
            <v-icon color="#FF6B35" class="mr-2">mdi-form-select</v-icon>
            <span>{{ $t("surveys") }}</span>
          </div>
          <v-divider></v-divider>
          <div class="pa-5">
            <questionnaire-settings-tab
              :questionares.sync="questionares"
              :has-questionare.sync="hasQuestionare"
            />
          </div>
        </div>
      </v-col>
    </v-row>
  </fullscreen-dialog>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import companyService from "@/services/company";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import BasicSettingsTab from "@/components/companies/settings/BasicSettingsTab.vue";
import ShiftOrderingSettingsTab from "@/components/companies/settings/ShiftOrderingSettingsTab.vue";
import WeeklyTimeSettingsTab from "@/components/companies/settings/WeeklyTimeSettingsTab.vue";
import QuestionnaireSettingsTab from "@/components/companies/settings/QuestionnaireSettingsTab.vue";
import {
  deliveryHours,
  deliveryMinutes,
  createWeeklyTimeSettings,
  createDefaultCompanySettings,
} from "@/models/timeSettings";

export default {
  name: "CompanySettingsDialog",
  mixins: [hasSnackbar],
  components: {
    FullscreenDialog,
    BasicSettingsTab,
    ShiftOrderingSettingsTab,
    WeeklyTimeSettingsTab,
    QuestionnaireSettingsTab,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    company: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localCompany: { settings: {} },
      questionares: [],
      hasQuestionare: false,
      deliveryHours,
      deliveryMinutes,
      weekDays: [
        { value: "monday", label: "monday" },
        { value: "tuesday", label: "tuesday" },
        { value: "wednesday", label: "wednesday" },
        { value: "thursday", label: "thursday" },
        { value: "friday", label: "friday" },
        { value: "saturday", label: "saturday" },
        { value: "sunday", label: "sunday" },
      ],
    };
  },
  computed: {
    user() {
      return this.$store.state.user.user;
    },
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.initializeCompany();
        if (this.user.type === "admin") {
          this.loadQuestionnaire();
        }
      }
    },
    company: {
      handler(newVal) {
        if (newVal && this.value) {
          this.initializeCompany();
        }
      },
      deep: true,
    },
  },
  methods: {
    initializeCompany() {
      // Deep clone company to avoid mutations
      this.localCompany = JSON.parse(JSON.stringify(this.company));

      // Initialize settings if null or missing
      if (this.localCompany.settings == null) {
        this.localCompany.settings = createDefaultCompanySettings();
      }

      if (
        typeof this.localCompany.settings != "string" &&
        !this.localCompany.settings.hasOwnProperty("shift_one")
      ) {
        this.localCompany.settings = createDefaultCompanySettings();
      }

      if (typeof this.localCompany.settings == "string") {
        if (!JSON.parse(this.localCompany.settings).order_from_one_shift_to_another) {
          JSON.parse(this.localCompany.settings)[
            "order_from_one_shift_to_another"
          ] = true;
        }
        this.localCompany.settings = JSON.parse(this.localCompany.settings);
      }

      if (!this.localCompany.settings.hasOwnProperty("global_restaurant_time_settings")) {
        this.localCompany.settings.global_restaurant_time_settings = false;
      }

      if (!this.localCompany.settings.hasOwnProperty("days")) {
        this.localCompany.settings.days = createWeeklyTimeSettings();
      }
    },
    changeOrderMealFromOneShiftToAnother() {
      if (this.localCompany.settings.order_from_one_shift_to_another) {
        this.localCompany.settings.order_from_one_shift_to_another_can = false;
        this.localCompany.settings.current_shift_meal = false;
      } else {
        this.localCompany.settings.order_from_one_shift_to_another_can = true;
      }
    },
    changeCurrentShiftMeal() {
      this.localCompany.settings.order_from_one_shift_to_another_can = !!this.localCompany
        .settings.current_shift_meal;
    },
    changeShowPrice() {
      companyService
        .changeShowPrice(this.localCompany)
        .then(() => {
          this.showSuccessSnackbar(this.$t("show_price_updated"));
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_updating_show_price"));
        });
    },
    loadQuestionnaire() {
      companyService
        .getQuestionnaire(this.company.id)
        .then((res) => {
          this.questionares = res.data && res.data.length ? res.data : [];
          if (res.data && res.data.length) this.hasQuestionare = true;
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_loading_questionnaire"));
        });
    },
    save() {
      companyService
        .storeSettings(this.localCompany)
        .then(({ data }) => {
          if (this.user.type === "admin") {
            companyService
              .saveQuestionnaire(this.company.id, this.questionares)
              .finally(() => (this.questionares = []));
          }

          if (data) {
            this.$emit("input", false);
            this.$emit("saved");
            this.showSuccessSnackbar(this.$t("company_settings_saved"));
          }
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_saving_company_settings"));
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/styles/_variables.scss";

.section-container {
  background: white;
  margin-bottom: 1px;
}

.section-title {
  background: white;
}
</style>
